import { useEditCompanyUserProfile } from "@/api/app/profile/useEditCompanyUserProfile";
import InputGroup from "@/components/InputGroup";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import useProfile from "@/hooks/useProfile";
import toast from "@/lib/toast";
import {
  CompanyUserProfileEdit,
  companyUserProfileEditSchema,
} from "@/schema/app/profile/CompanyUser";
import { SpinnerIosRegular } from "@fluentui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";

interface EditProfileDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

function EditProfileDialog({ open, setOpen }: EditProfileDialogProps) {
  const { profile } = useProfile();
  const { t } = useTranslation();

  const methods = useForm<CompanyUserProfileEdit>({
    resolver: zodResolver(companyUserProfileEditSchema),
    values: {
      name: profile?.name ?? "",
      email: profile?.email ?? "",
    },
  });

  const {
    handleSubmit,
    formState: { errors, isDirty },
    register,
  } = methods;

  const { trigger: editCompanyUserProfile, isMutating } =
    useEditCompanyUserProfile({
      onSuccess: (data) => {
        setOpen(false);
        toast.success(data.message);
        mutate("/app/assessment");
      },
    });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("edit-profile")}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit((data) => {
              editCompanyUserProfile(data);
            })();
          }}
        >
          <div className="flex flex-col gap-6 px-6 pb-5 pt-4">
            <InputGroup
              label={t("email")}
              type="email"
              error={errors.email?.message}
              register={register("email", {
                required: t("email-required"),
              })}
            />
            <InputGroup
              label={t("name")}
              error={errors.name?.message}
              register={register("name", {
                required: t("name-required"),
              })}
            />
            <div className="flex flex-col gap-2">
              <div className="text-red-500 text-sm">
                {t("edit-profile-warning")}
              </div>
              <Button
                LoadingComponent={
                  <SpinnerIosRegular className="animate-spin" />
                }
                isLoading={isMutating}
                type="submit"
                disabled={!isDirty}
              >
                {t("save")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditProfileDialog;
